import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  isCustomizationModalOpen,
  isConfirmationModalOpen,
  closeCustomizationModal,
  closeConfirmationModal,
  openCustomizationModal,
  resetCustomization,
  userWantsToCustomize,
  config,
  isCustomTextValid,
  customText,
  previewImageUrl,
  isLoadingPreview,
  shouldShowCustomizeButton,
  getCustomSkuShipsBy,
} from "../store/customization-store";
import { productData, selectedVariant, selectedQuantity, isAvailable } from "../store/product-store";
import { isExtendedOfferEnabled } from "../store/extended-offer-store";
import {
  registerModal,
  startModalChain,
  activeModal,
  approveModal,
  cancelModal,
  modalContext,
  type ModalContext,
} from "../store/modal-store";
import { DialogA11y, prefersReducedMotion } from "../controllers/dialog-a11y";

/**
 * Customization Modal Component
 *
 * Wrapper component that manages product customization modals.
 * Only renders when product has "customizable" tag.
 *
 * Features:
 * - Confirmation modal (asks if user wants to customize)
 * - Customization modal (text input + live preview)
 * - Integrates with modal orchestrator
 * - Sub-components for inputs and image upload
 *
 * Usage in Liquid:
 * <customization-modal
 *   data-custom-sku-variant-id="12345"
 *   data-preview-url="https://..."
 *   data-is-mandatory="true"
 * ></customization-modal>
 */
const FADE_DURATION_MS = 300;

@customElement("customization-modal")
export class CustomizationModal extends LitElement {
  @state() private _isCustomizationOpen = false;
  @state() private _isConfirmationOpen = false;
  @state() private _showCustomizeButton = false;
  @state() private _isExtendedOffer = false;
  @state() private _isTextValid = false;
  @state() private _previewUrl: string | null = null;
  @state() private _isLoadingPreview = false;
  @state() private _productTitle = "";
  @state() private _variantTitle = "";
  @state() private _isClosing = false;
  @state() private _isAnimatedIn = false;
  @state() private _isDisclaimerOpen = false;
  private _closeTimeoutId: number | null = null;

  /**
   * This component hosts two dialogs, so it needs two independent focus traps —
   * which is why it uses the DialogA11y controller directly instead of
   * extending ModalBase (which is bound to a single modal name).
   */
  private _confirmationA11y = new DialogA11y(this, {
    getDialog: () => this.querySelector<HTMLElement>(".confirmation-dialog"),
    onDismiss: () => this._handleCloseConfirmation(),
    getReturnFocus: () => modalContext.value?.triggerButton ?? null,
  });

  private _customizationA11y = new DialogA11y(this, {
    getDialog: () => this.querySelector<HTMLElement>(".customization-dialog"),
    onDismiss: () => this._handleCancelCustomization(),
    getReturnFocus: () => modalContext.value?.triggerButton ?? null,
  });

  constructor() {
    super();

    // Watch activeModal signal — open the right modal when this component is
    // activated by the modal-store chain.
    effect(() => {
      const active = activeModal.value;
      if (active === "custom-text-customization") {
        openCustomizationModal();
      } else if (active === "custom-text-confirmation") {
        isConfirmationModalOpen.value = true;
      }
    });

    // Subscribe to store
    effect(() => {
      this._isCustomizationOpen = isCustomizationModalOpen.value;
      this._isConfirmationOpen = isConfirmationModalOpen.value;
      this._showCustomizeButton = shouldShowCustomizeButton.value;
      // Sold-out (extended-offer) variants can't be added to cart, so personalizing
      // them is pointless — grey out and disable the "Personalize it" button.
      this._isExtendedOffer = !isAvailable.value && Boolean(isExtendedOfferEnabled.value);
      this._isTextValid = isCustomTextValid.value;
      this._previewUrl = previewImageUrl.value;
      this._isLoadingPreview = isLoadingPreview.value;
      this._productTitle = productData.value?.title || "";
      this._variantTitle = selectedVariant.value?.title || "";

      if (!this._isCustomizationOpen) {
        this._isAnimatedIn = false;
        this._isDisclaimerOpen = false;
      }

      // Focus trap, Escape, scroll lock and focus restoration for each dialog.
      this._confirmationA11y.setOpen(this._isConfirmationOpen);
      this._customizationA11y.setOpen(this._isCustomizationOpen);

      this.requestUpdate();
    });
  }


  connectedCallback() {
    super.connectedCallback();

    // Read configuration from attributes
    const customSkuVariantId = this.getAttribute("data-custom-sku-variant-id");
    const previewUrl = this.getAttribute("data-preview-url");
    const isMandatory = this.getAttribute("data-is-mandatory") === "true";

    config.value = {
      ...config.value,
      customSkuVariantId,
      previewUrl,
      isMandatory,
    };

    // Register modals with orchestrator
    this._registerModals();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._closeTimeoutId) {
      clearTimeout(this._closeTimeoutId);
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated?.(changedProperties);
    if (this._isCustomizationOpen && !this._isClosing && !this._isAnimatedIn) {
      requestAnimationFrame(() => {
        this._isAnimatedIn = true;
      });
    }
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this; // Light DOM for Tailwind
  }

  private _registerModals() {
    // The customization config/state lives in the main product's store, so these
    // modals must only ever fire for the main product page CTA. Collection cards
    // (e.g. recommended products) run the same shared modal chain, so guard on the
    // context's product id — otherwise a customizable main product would pop the
    // customization modal when adding an unrelated card to the cart.
    const isMainProductContext = (context: ModalContext) =>
      context.productId !== undefined &&
      context.productId === productData.value?.id;

    // Register confirmation modal (priority 40 - after holiday shipping)
    registerModal({
      name: "custom-text-confirmation",
      priority: 40,
      shouldShow: (context) => {
        if (!isMainProductContext(context)) return false;
        // Only show if customization is optional (not mandatory) and user hasn't chosen yet
        if (context.userWantsToCustomize || config.value.isMandatory) {
          return false;
        }
        return shouldShowCustomizeButton.value;
      },
    });

    // Register customization modal (priority 50 - after confirmation)
    registerModal({
      name: "custom-text-customization",
      priority: 50,
      shouldShow: (context) => {
        if (!isMainProductContext(context)) return false;
        // Show if user wants to customize OR customization is mandatory
        return context.userWantsToCustomize || config.value.isMandatory;
      },
    });
  }

  // ─── Confirmation Modal Handlers ──────────────────────────────────────────

  private _handleYesCustomize() {
    closeConfirmationModal();
    userWantsToCustomize.value = true;
    // Mark context so custom-text-customization.shouldShow returns true
    if (modalContext.value) {
      modalContext.value.userWantsToCustomize = true;
    }
    approveModal();
  }

  private _handleNoAddToCart() {
    closeConfirmationModal();
    userWantsToCustomize.value = false;
    // Advance chain — customization modal won't show because !userWantsToCustomize && !isMandatory
    approveModal();
  }

  private _handleCloseConfirmation() {
    closeConfirmationModal();
    cancelModal();
  }

  // ─── Customization Modal Handlers ─────────────────────────────────────────

  private _handleAddToCart() {
    // The button stays focusable while the text is invalid (so keyboard users
    // can reach it and hear why it is unavailable), so enforce validity here
    // rather than relying on the `disabled` attribute.
    if (!this._isTextValid) return;

    const ctx = modalContext.value;
    if (ctx && customText.value.trim()) {
      if (!ctx.customization) {
        ctx.customization = {};
      }
      ctx.customization["Your Text"] = customText.value;

      // Personalized items ship later than the base product, so the customized
      // line item takes its ships-by date from the custom SKU product's tag
      // (falling back to the default shipping date), overriding the main
      // product's date that add-to-cart.ts may have set on the context.
      const customShipsBy = getCustomSkuShipsBy();
      if (customShipsBy) {
        ctx.customization["_ships-by"] = customShipsBy;
      }

      // If useCustomSku is enabled, pass the custom SKU variant ID so
      // executeAddToCart can add it as a second line item (personalization fee)
      if (config.value.useCustomSku && config.value.customSkuVariantId) {
        // Support both plain numeric IDs and Shopify GID strings
        // (e.g. "gid://shopify/ProductVariant/12345" → 12345)
        const raw = config.value.customSkuVariantId;
        const id = parseInt(raw.split("/").pop() ?? raw, 10);
        if (!isNaN(id)) {
          ctx.customSkuVariantId = id;
        }
      }
    }

    resetCustomization();
    approveModal();
  }

  private _handleCancelCustomization() {
    this._closeWithAnimation();
  }

  private _closeWithAnimation() {
    if (this._isClosing) return;
    this._isClosing = true;

    const finish = () => {
      this._closeTimeoutId = null;
      this._isClosing = false;
      this._isAnimatedIn = false;
      resetCustomization();
      closeCustomizationModal();
      cancelModal();
    };

    // Waiting out the fade would also delay the focus return, so close
    // immediately when the user has asked for reduced motion.
    if (prefersReducedMotion()) {
      finish();
      return;
    }

    this._closeTimeoutId = window.setTimeout(finish, FADE_DURATION_MS);
  }

  private _handleOpenCustomization(e: Event) {
    userWantsToCustomize.value = true;
    const variant = selectedVariant.value;
    let imageUrl = variant?.featured_image?.src ||
      variant?.image ||
      productData.value?.featured_image ||
      "";
    if (imageUrl.startsWith("//")) {
      imageUrl = "https:" + imageUrl;
    }
    imageUrl = imageUrl.split('?')[0];

    startModalChain({
      productId: productData.value?.id,
      variantId: variant?.id,
      quantity: selectedQuantity.value,
      productTitle: productData.value?.title,
      variantTitle: variant?.title,
      vendor: productData.value?.vendor,
      imageUrl,
      customization: {},
      userWantsToCustomize: true,
      source: "customize-button",
      // Where focus returns when the modal closes.
      triggerButton: e.currentTarget as HTMLButtonElement,
    });
  }

  // ─── Disclaimer Toggle ────────────────────────────────────────────────────

  private _handleDisclaimerToggle() {
    this._isDisclaimerOpen = !this._isDisclaimerOpen;
  }

  // ─── Render Methods ───────────────────────────────────────────────────────

  /**
   * Alt text for the live preview. Includes the entered text so screen reader
   * users get the same confirmation of what was rendered that sighted users do.
   */
  private _previewAltText() {
    const text = customText.value.trim();
    const product = this._productTitle || "product";
    return text
      ? `Preview of your ${product} personalized with "${text}"`
      : `Preview of your ${product}, not yet personalized`;
  }

  private _renderCustomizeButton() {
    if (!this._showCustomizeButton) return html``;

    return html`
      <div class="product__custom-sku bg-[var(--color-secondary-100)] rounded p-4 my-4 gap-3 flex flex-col">
        <div class="container flex flex-wrap justify-between items-center gap-x-4 gap-y-1">
          <h4 class="text-sm font-bold text-[var(--color-secondary-800)] md:text-base">Personalize your charger</h4>
          <p class="text-caption text-[var(--color-neutral-950)]">
            With extra cost $${config.value.customSkuCost.toFixed(2)}
            <span class="font-bold">optional</span>
          </p>
        </div>
        <button
          @click=${this._handleOpenCustomization}
          class="button w-full btn btn w-full bg-[var(--color-secondary-900)] hover:bg-[var(--color-secondary-800)] ${this
        ._isExtendedOffer
        ? "opacity-50 cursor-not-allowed pointer-events-none"
        : ""}"
          type="button"
          ?disabled=${this._isExtendedOffer}
          aria-disabled=${this._isExtendedOffer}
        >
          Personalize it
        </button>
      </div>
    `;
  }

  private _renderConfirmationModal() {
    if (!this._isConfirmationOpen) return html``;

    return html`
      <div
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm"
        @click=${this._handleCloseConfirmation}
      >
        <div
          class="confirmation-dialog bg-[var(--color-secondary-50)] rounded-lg shadow-xl max-w-xl w-full p-6 relative"
          @click=${(e: Event) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirmation-title"
        >
          <button
            @click=${this._handleCloseConfirmation}
            class="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded text-xl leading-none text-[var(--color-secondary-600)] hover:opacity-90 transition-opacity"
            aria-label="Close"
            type="button"
          >
            <span aria-hidden="true">×</span>
          </button>
          <div class="confirmation-modal__content">
            <div class="confirmation-modal__header mb-6 pr-8">
              <h3 id="confirmation-title" class="text-xl font-bold text-[var(--color-neutral-950)]">
                Make it yours and personalize your charger?
              </h3>
            </div>
            <div class="confirmation-modal__buttons flex flex-col min-[560px]:flex-row gap-3">
              <button
                @click=${this._handleYesCustomize}
                class="button btn w-full min-[560px]:flex-1 border border-[var(--color-secondary-500)] bg-[var(--color-secondary-50)] text-[var(--color-secondary-600)] hover:opacity-90 transition-opacity"
                type="button"
              >
                Yes, personalize it
              </button>
              <button
                @click=${this._handleNoAddToCart}
                class="button btn w-full min-[560px]:flex-1"
                type="button"
              >
               Add without personalization
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderCustomizationModal() {
    if (!this._isCustomizationOpen) return html``;

    const isVisible = this._isAnimatedIn && !this._isClosing;

    return html`
      <div
        class="fixed inset-0 z-50 flex items-end lg:items-center justify-center p-4 bg-black/20 backdrop-blur-sm transition-opacity duration-300 ease-out ${isVisible ? "opacity-100" : "opacity-0"}"
        @click=${this._handleCancelCustomization}
      >
        <div
          class="customization-dialog bg-[var(--color-secondary-50)] rounded-lg shadow-xl w-[90vw] lg:w-[70vw] max-h-[90vh] overflow-hidden flex flex-col relative transition-all duration-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full"}"
          @click=${(e: Event) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="customization-title"
        >
          <button
            @click=${this._handleCancelCustomization}
            class="absolute right-[10px] top-[10px] lg:right-auto lg:left-[20px] lg:top-[20px] z-10 flex items-center justify-center px-3 py-2 rounded bg-[var(--color-secondary-50)] text-[var(--color-secondary-600)] font-medium text-sm hover:opacity-90 transition-opacity"
            type="button"
          >
            Close
          </button>

          <!-- Content: Image | Form -->
          <div class="flex min-h-0 flex-1 flex-col lg:flex-row lg:overflow-hidden">
            <!-- Preview Image (left) -->
            <div class="lg:w-1/2 flex items-center justify-center bg-white rounded lg:rounded-r-none relative min-h-[200px] lg:min-h-[60vh]">
              ${this._isLoadingPreview
        ? html`
                    <div class="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg" role="status">
                      <div class="w-8 h-8 border-4 border-[var(--color-secondary-500)] border-t-transparent rounded-full animate-spin"></div>
                      <span class="sr-only">Loading preview</span>
                    </div>
                  `
        : ""}
              ${this._previewUrl
        ? html`
                    <img
                      src="${this._previewUrl}"
                      alt=${this._previewAltText()}
                      class="max-w-full max-h-[400px] lg:max-h-[90vh] object-contain rounded-lg"
                    />
                  `
        : html`
                    <div class="text-[var(--color-neutral-400)] text-sm p-8">
                      Preview will appear here
                    </div>
                  `}
            </div>

            <!-- Form panel (right) -->
            <div class="flex min-h-0 min-w-0 flex-1 flex-col p-3 md:p-8">
              <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto">
              <!-- Header -->
              <div class="flex min-w-0 flex-col gap-6">
                <div class="flex min-w-0 flex-wrap items-center gap-4">
                  <h3 id="customization-title" class="min-w-0 max-w-full break-words text-xl font-bold text-[var(--color-secondary-800)] md:text-3xl">
                    Customize your ${this._productTitle}
                  </h3>
                  <p class="variant-label small max-w-full shrink-0">
                    ${this._variantTitle}
                  </p>
                </div>

                <!-- Custom Text Input -->
                <custom-text-input></custom-text-input>
              </div>

              <!-- Disclaimer -->
              <div class="product__customized-disclaimer flex flex-col rounded-[5px] overflow-hidden bg-[var(--color-secondary-100)]">
                <h4 class="shrink-0">
                  <button
                    @click=${this._handleDisclaimerToggle}
                    class="disclaimer-toggle w-full p-3 md:p-4 md:text-xs text-left flex items-center justify-between gap-2 hover:opacity-90 transition-opacity bg-[var(--color-secondary-100)] rounded-md text-sm font-bold text-[var(--color-secondary-800)] md:text-base"
                    type="button"
                    id="customization-disclaimer-toggle"
                    aria-expanded=${this._isDisclaimerOpen ? "true" : "false"}
                    aria-controls="customization-disclaimer-content"
                  >
                    Customization &amp; Returns/Exchange Policy
                    <span class="toggle-icon text-[var(--color-secondary-500)] font-bold text-xl" aria-hidden="true"
                      >${this._isDisclaimerOpen ? "−" : "+"}</span
                    >
                  </button>
                </h4>
                <div
                  id="customization-disclaimer-content"
                  role="region"
                  aria-labelledby="customization-disclaimer-toggle"
                  class="disclaimer-content min-h-0 px-4 pt-0 pb-4 text-sm text-[var(--color-secondary-700)] space-y-4 max-h-[180px] overflow-y-scroll"
                  ?hidden=${!this._isDisclaimerOpen}
                >
                  <p>
                    Items that are <strong>personalized to your specifications</strong> cannot be
                    returned or exchanged, and all sales of personalized items are considered final.
                  </p>
                  <p>
                    If you receive a personalized item that is <strong>damaged</strong>, contains the
                    <strong>wrong initial</strong>, or if <strong>we've made an error</strong> in
                    customization, please contact us within <strong>14 days of receiving your order</strong>,
                    and we will gladly remake the item for you.
                  </p>
                  <p>
                    <strong>Non-personalized items</strong> are eligible for return or exchange in
                    accordance with our standard return policy.
                  </p>
                </div>
              </div>
              </div>

              <!-- Footer Buttons -->
              <div class="flex shrink-0 gap-2.5 pt-4">
                <button
                  @click=${this._handleCancelCustomization}
                  class="btn w-1/2 bg-[var(--color-secondary-100)] text-[var(--color-secondary-500)]"
                  type="button"
                >
                  <span>Cancel</span>
                </button>
                <button
                  @click=${this._handleAddToCart}
                  aria-disabled=${this._isTextValid ? "false" : "true"}
                  aria-describedby=${this._isTextValid ? nothing : "customization-text-hint"}
                  class="btn w-1/2 ${this._isTextValid
        ? "bg-[var(--color-primary-500)] hover:opacity-90"
        : "bg-[var(--color-neutral-300)] text-[var(--color-neutral-500)] cursor-not-allowed"}"
                  type="button"
                >
                  Add to cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      ${this._renderCustomizeButton()}
      ${this._renderConfirmationModal()}
      ${this._renderCustomizationModal()}
    `;
  }
}
